import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { conciergeChat } from "../api";

const ConciergeWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm the Anadear Estates concierge. How can I help with your property search in Bath or Bristol today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("anadear:open-concierge", openHandler);
    return () => window.removeEventListener("anadear:open-concierge", openHandler);
  }, []);

  const sendText = async (text) => {
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const data = await conciergeChat(text, sessionId);
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond just now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    sendText(input.trim());
  };

  const QUICK = ["Find me a 2-bed in Bath", "Book a free valuation", "What's the Bath market like?", "Help me sell my home"];

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open concierge chat"
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-[#b8935f] flex items-center justify-center shadow-[0_6px_20px_rgba(184,147,95,0.45)] hover:scale-105 transition-transform"
      >
        {open ? <X size={24} className="text-white" /> : <MessageCircle size={24} className="text-white" />}
      </button>

      {open && (
        <div className="fixed bottom-44 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[70vh] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.22)] border border-[#f0ece5] flex flex-col overflow-hidden">
          <div className="bg-[#1c1c1e] px-5 py-4 flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#b8935f] flex items-center justify-center">
              <Sparkles size={17} className="text-white" />
            </span>
            <div>
              <p className="text-white text-[14px] font-500">Anadear Concierge</p>
              <p className="text-white/50 text-[11px]">AI-powered property assistant</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#faf7f2]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#b8935f] text-white rounded-br-sm"
                    : "bg-white text-[#3a3a3a] border border-[#f0ece5] rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#f0ece5] px-4 py-3 rounded-2xl rounded-bl-sm">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#b8935f] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#b8935f] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#b8935f] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="concierge-quick-replies">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => sendText(q)} className="px-3 py-1.5 rounded-full bg-white border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">{q}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-[#f0ece5] flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about properties, valuations..."
              className="flex-1 h-[44px] px-4 rounded-full border border-[#e2d8c8] text-[13.5px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]"
            />
            <button type="submit" disabled={loading} className="w-11 h-11 rounded-full bg-[#b8935f] flex items-center justify-center hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
              <Send size={17} className="text-white" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ConciergeWidget;
